import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider } from "@mui/material";
import React from "react";
import {
  COLUMN_NAME_CLICKS,
  COLUMN_NAME_CPA,
  COLUMN_NAME_CTR,
  COLUMN_NAME_IMPRESSIONS,
  COLUMN_NAME_RESULTS,
  COLUMN_NAME_SPEND,
  FACEBOOK,
  INSTAGRAM,
  THREADS,
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";

/**
 * Exclude Meta age breakdown rows not shown in the audience table:
 * `unknown`, and aggregate bucket `65+` (case-insensitive; spaces ignored).
 */
//eslint-disable-next-line
export const filterAgeDemographicInsightsRows = (rows) =>
  (rows ?? []).filter((row) => {
    const age = row?.age;
    if (age === null || age === undefined) return true;
    const normalized = String(age).trim().toLowerCase().replace(/\s/g, "");
    if (normalized === "unknown") return false;
    if (normalized === "65+") return false;
    return true;
  });

//eslint-disable-next-line
export const filterGenderInsightsRowsWithoutUnknown = (rows) =>
  (rows ?? []).filter((row) => {
    const gender = row?.gender;
    if (gender === null || gender === undefined) return true;
    return String(gender).trim().toLowerCase() !== "unknown";
  });

//eslint-disable-next-line
export const PUBLISHER_PLATFORM_LABEL_KEYS = {
  facebook: FACEBOOK,
  instagram: INSTAGRAM,
  threads: THREADS,
};

const formatPublisherPlatformFallbackLabel = (publisherPlatform) => {
  const normalized = String(publisherPlatform).trim().toLowerCase();
  if (!normalized) return "—";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

//eslint-disable-next-line
export const getPublisherPlatformRowLabel = ({ publisherPlatform, t }) => {
  const normalized = String(publisherPlatform ?? "")
    .trim()
    .toLowerCase();
  const labelKey = PUBLISHER_PLATFORM_LABEL_KEYS[normalized];
  if (labelKey) return t(labelKey);
  return formatPublisherPlatformFallbackLabel(publisherPlatform);
};

//eslint-disable-next-line
export const sortPublisherPlatformInsightsRows = (rows) =>
  [...(rows ?? [])]
    .filter((row) => {
      const publisherPlatform = row?.publisherPlatform;
      if (!publisherPlatform) return false;
      return String(publisherPlatform).trim().toLowerCase() !== "unknown";
    })
    .toSorted((a, b) => (b?.spend ?? 0) - (a?.spend ?? 0));

/** Same idea as TopKeywords in GooglePerformingAssets: short label + "." */
const shortMetricHeader = (fullLabel) => {
  if (!fullLabel) return "";
  const trimmed = String(fullLabel).trim();
  if (trimmed.length <= 5) return trimmed;
  return `${trimmed.slice(0, 4)}.`;
};

const METRIC_COLUMNS = [
  {
    key: "impressions",
    labelKey: COLUMN_NAME_IMPRESSIONS,
    format: "number",
    accessor: (row) => row.impressions,
  },
  {
    key: "click",
    labelKey: COLUMN_NAME_CLICKS,
    format: "number",
    accessor: (row) => row.click,
  },
  {
    key: "ctr",
    labelKey: COLUMN_NAME_CTR,
    format: "percentage",
    accessor: (row) => row.ctr,
  },
  {
    key: "spend",
    labelKey: COLUMN_NAME_SPEND,
    format: "spend",
    accessor: (row) => row.spend,
  },
  {
    key: "mainAction",
    labelKey: COLUMN_NAME_RESULTS,
    format: "number",
    accessor: (row) => row.mainAction,
  },
  {
    key: "costPerMainAction",
    labelKey: COLUMN_NAME_CPA,
    format: "cpa",
    accessor: (row) => row.costPerMainAction,
  },
];

/** First column = 2/12 of width (12fr of 72); six equal metric columns (10fr each). */
const GRID_TEMPLATE = "minmax(0, 12fr) repeat(6, minmax(0, 10fr))";

const metricGridColumn = (index) => ({
  gridColumn: index + 2,
});

const formatMetricCell = ({ row, metric, t, getCostByMainCurrency }) => {
  const raw = metric.accessor(row);
  const currency = row.currency;

  switch (metric.format) {
    case "number":
      return t(VALUE_NUMBER, { value: raw ?? 0 });
    case "percentage":
      return t(VALUE_WITH_PERCENTAGE, { value: raw ?? 0 });
    case "spend": {
      const { cost, currency: outCurrency } = getCostByMainCurrency({
        cost: raw,
        currency,
      });
      return t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: cost,
        currency: outCurrency,
      });
    }
    case "cpa": {
      const mainAction = row.mainAction;
      if (!mainAction || Number(mainAction) <= 0) return "-";
      const { cost, currency: outCurrency } = getCostByMainCurrency({
        cost: raw,
        currency,
      });
      return renderMetricValue(cost, "costPerMainAction", outCurrency);
    }
    default:
      return String(raw ?? "");
  }
};

const cellBaseSx = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

/**
 * Single CSS Grid template: segment column 2/12 width; six equal metric columns.
 * Same template in header and rows for strict alignment.
 */
export const DemographicInsightsGrid = ({
  title,
  rows,
  getRowLabel,
  getCostByMainCurrency,
  sx: rootSx = {},
}) => {
  const { t } = useTranslationApp();

  if (!rows?.length) return null;

  return (
    <Box sx={{ width: "100%", overflowX: "auto", ...rootSx }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_TEMPLATE,
          columnGap: 1,
          rowGap: 1,
          mb: 2,
          minWidth: { xs: 480, sm: "100%" },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ ...cellBaseSx, gridColumn: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            {title}
          </Typography>
        </Box>
        {METRIC_COLUMNS.map((metric, i) => (
          <Box key={metric.key} sx={{ ...cellBaseSx, ...metricGridColumn(i) }}>
            <Typography
              variant="caption"
              component="p"
              fontWeight={400}
              align="right"
              color="fields.placeholder"
              sx={{ width: "100%", m: 0 }}
            >
              {shortMetricHeader(t(metric.labelKey))}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ gridColumn: "1 / -1", my: 0.25 }} />

        {rows.map((row, idx) => (
          <React.Fragment key={`${getRowLabel(row)}-${idx}`}>
            <Box sx={{ ...cellBaseSx, gridColumn: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  ml: 0.5,
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getRowLabel(row)}
              </Typography>
            </Box>
            {METRIC_COLUMNS.map((metric, i) => (
              <Box
                key={`${metric.key}-${idx}`}
                sx={{ ...cellBaseSx, ...metricGridColumn(i) }}
              >
                <Typography
                  variant="body2"
                  align="right"
                  sx={{ width: "100%" }}
                >
                  {formatMetricCell({
                    row,
                    metric,
                    t,
                    getCostByMainCurrency,
                  })}
                </Typography>
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
